'use client'

import { useRef } from 'react'
import { InView } from 'react-intersection-observer'
import classNames from 'classnames'
import { Spacer } from '@vinted/web-ui'

import { HorizontalScrollArea } from '@marketplace-web/common-components/horizontal-scroll-area-ui'
import {
  getHomepageSessionIdFromUrl,
  withHomepageSessionIdFromUrl,
} from '@marketplace-web/home/home-page-util'
import { TrackedProductItem } from '@marketplace-web/item-box/item-box-feature'
import { ItemSearchParamsDto, impressionEvent } from '@marketplace-web/item-page/item-page-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { TrackedSellerProductItem } from '@marketplace-web/seller-item-box/seller-item-box-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'

type ItemModel = ProductItemModel & { search_tracking_params?: ItemSearchParamsDto | null }

type Props = {
  items: Array<ItemModel>
  contentSource: Required<ProductItemModel>['contentSource']
  horizontalScrollEnabled?: boolean
  itemTestId?: string
}

const ItemViewItems = ({
  items,
  contentSource,
  itemTestId,
  horizontalScrollEnabled = false,
}: Props) => {
  const { track } = useTracking()
  const seenItemIds = useRef<Array<number>>([])
  const currentUserId = useSession().user?.id
  const { searchParams } = useBrowserNavigation()
  const translateA11y = useTranslate('a11y')

  const searchSessionData = getSearchSessionData()
  const searchSessionIdRef = useRef(
    searchParams.referrer?.toString() === 'catalog' ? searchSessionData.searchSessionId : null,
  )

  const homepageSessionId = getHomepageSessionIdFromUrl()

  const handleItemVisibility = (item: ItemModel, position: number) => (inView: boolean) => {
    if (!inView) return

    const { id, user } = item
    const { id: itemOwnerId } = user
    const searchTrackingParams =
      'search_tracking_params' in item ? item.search_tracking_params : null

    if (seenItemIds.current.includes(id)) return

    track(
      impressionEvent({
        id,
        position,
        contentSource,
        itemOwnerId,
        globalSearchSessionId: searchSessionData.globalSearchSessionId,
        globalCatalogBrowseSessionId: searchSessionData.globalCatalogBrowseSessionId,
        searchSessionId: searchSessionIdRef.current,
        contentType: 'item',
        searchScore: searchTrackingParams?.score,
        searchSignals: searchTrackingParams?.matched_queries,
        itemDistance: searchTrackingParams?.buyer_item_distance_km,
        homepageSessionId,
      }),
    )

    seenItemIds.current.push(id)
  }

  const renderItem = (item: ItemModel, index: number) => {
    const position = index + 1
    const { id } = item
    const searchTrackingParams =
      'search_tracking_params' in item ? item.search_tracking_params : null
    const trackingArgs = {
      id,
      position,
      contentSource,
      globalSearchSessionId: searchSessionData.globalSearchSessionId,
      searchSessionId: searchSessionIdRef.current,
      contentType: 'item',
      searchScore: searchTrackingParams?.score,
      searchSignals: searchTrackingParams?.matched_queries,
      itemDistance: searchTrackingParams?.buyer_item_distance_km,
      homepageSessionId,
    } satisfies React.ComponentProps<
      typeof TrackedSellerProductItem | typeof TrackedProductItem
    >['tracking']

    const isViewingSelf = currentUserId === item.user.id
    const ProductItemElement = isViewingSelf ? TrackedSellerProductItem : TrackedProductItem

    const productItemProps = {
      url: withHomepageSessionIdFromUrl(item.url),
      showStatus: true,
      ...(!isViewingSelf && {
        showFavourite: true,
        isBumpTextHidden: true,
      }),
    }

    return (
      <InView
        onChange={handleItemVisibility(item, position)}
        key={id}
        className={classNames({
          'item-view-items__item': !horizontalScrollEnabled,
        })}
      >
        <ProductItemElement
          item={item}
          testId={itemTestId && `${itemTestId}-${id}`}
          tracking={trackingArgs}
          {...productItemProps}
        />
      </InView>
    )
  }

  if (horizontalScrollEnabled)
    return (
      <div data-testid={`${itemTestId}-items`} className="item-page-horizontal-block">
        <Spacer size="large" />
        <HorizontalScrollArea
          itemsFullWidthAlignment
          controlsScrollType={HorizontalScrollArea.ControlScrollType.Partial}
          arrowLeftText={translateA11y('actions.move_left')}
          arrowRightText={translateA11y('actions.move_right')}
        >
          {items.map((item, index) => (
            <HorizontalScrollArea.Item className="item-view-items-horizontal__item" key={item.id}>
              {renderItem(item, index)}
            </HorizontalScrollArea.Item>
          ))}
        </HorizontalScrollArea>
      </div>
    )

  return <div className="item-view-items">{items.map(renderItem)}</div>
}

export default ItemViewItems
